import { css } from "@emotion/react";
import { Fragment, useMemo } from "react";

import { Paginator, SkeletonBlock } from "@zapier/design-system";
import { addTruthyParamsToUrl } from "@zapier/url-utils";

import List from "@zapier/reporting/src/components/global/List";
import { MAX_RUN_SELECTION } from "@zapier/reporting/src/constants";
import { SizeType } from "@zapier/reporting/src/hooks";
import { ZapRun } from "@zapier/reporting/src/hooks/useRuns";
import { getResponsiveStyles } from "@zapier/reporting/src/utils";
import RunRow from "./RunRow";

export type Props = {
  isEmbed: boolean;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onSelect: (id: string, isSelected: boolean) => void;
  page: number;
  runCount: number;
  runs: Array<ZapRun>;
  selectedAll: boolean;
  selectedIds: Array<string>;
  showOwner: boolean;
  showZapDetails: boolean;
  size: SizeType;
  url: string;
};

const pageStyle = css`
  display: flex;
  justify-content: center;
`;

const rootStyle = getResponsiveStyles({
  tablet: css`
    grid-gap: 20px;
    overflow: unset;
  `,
  mobile: css`
    display: grid;
    grid-gap: 10px;
    overflow: hidden;
  `,
});

function Loader() {
  return (
    <div css={rootStyle}>
      {Array.from({ length: 10 }, (_, i) => (
        <SkeletonBlock height="72px" key={i} width="100%" />
      ))}
    </div>
  );
}

const pageSize = 10;

function RunList(props: Props) {
  const { selectedAll } = props;
  const needsPagination = !props.isLoading && props.runCount > pageSize;

  const pageCount = props.isLoading ? 1 : Math.ceil(props.runCount / pageSize);

  const pageUrls = useMemo(() => {
    return Array.from({ length: pageCount }, (_, i) =>
      addTruthyParamsToUrl(props.url, { page: i + 1 })
    );
  }, [pageCount, props.url]);

  function handlePage(data: { pageIndex: number }) {
    props.onPageChange(data.pageIndex + 1);
  }

  return (
    <div css={rootStyle} data-testid="TaskList">
      {props.isLoading ? (
        <Loader />
      ) : (
        <Fragment>
          <List
            gap={20}
            id="TaskList_List"
            itemRenderer={(run: ZapRun, index: number) => (
              <RunRow
                isSelected={
                  props.selectedIds.includes(run.id) ||
                  (selectedAll &&
                    pageSize * props.page + index < MAX_RUN_SELECTION)
                }
                onSelect={props.onSelect}
                run={run}
                showOwner={props.showOwner}
                showZapDetails={props.showZapDetails}
                isEmbed={props.isEmbed}
              />
            )}
            items={props.runs}
          />
          {needsPagination && (
            <div css={pageStyle}>
              <Paginator
                currentPageIndex={props.page - 1}
                isCentered={props.size === "small"}
                onPaginate={handlePage}
                pageUrls={pageUrls}
              />
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
}

export default RunList;
